import React from "react";
import { useState } from "react";
import {
  BlobSasRequestDto,
  DuplicateFilesDto,
  GetAllFilesAndFoldersByUserDto,
  GetFoldersByUserIdDto,
} from "../types/FolderPermission";
import { DeleteOrPublishTypeDto, DeleteTypeDto, DuplicateDto } from "../types/Folder";
import { GLOBALSETTINGS, SEVERITIES } from "../data/constants";
import useApi from "./useApi";
import { useDispatch } from "react-redux";
import { deleteFile, deleteFolder, unPublishFolderOrFile } from "../services/folderService";
import { setResourceDeleted } from "../redux/resourceDeletedSlice";
import { FileActions, FolderFileType } from "../data/Enums";
import { useToaster } from "./useToaster";
import { duplicateFile, duplicateFolder } from "../services/duplicateService";
import { ResponseDto } from "../types/Helper";
import { setFolder } from "../redux/folderSlice";
import { logAuditTrace, removeRecentViewedItem } from "../services/auditNavigationTraceService";
import {
  DELETE_FAIL,
  DELETE_SUCCESSFUL,
  DESCRIPTION_LENGTH,
  DUPLICATEVALIDATIONS,
  GLOBALMESSAGES,
  UNPUBLISH_FAIL,
  UNPUBLISH_SUCCESSFUL,
} from "../constants/messages";
import { getCurrentESTTime } from "../utils/dateHelper";
import { getBlobSasUri } from "../services/downloadService";
import { useUser } from "../context/UserContext";
import { setResourceAdded } from "../redux/resourceAddedSlice";

const useMenuActions = (
  userId: number,
  currentRow: GetAllFilesAndFoldersByUserDto | null,
  handleMenuClose: () => void
) => {
  const [downloadData, setDownloadData] = useState({
    userId: null as number | null,
    folderId: null as number | null,
    folderName: "",
  });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [unPublishConfirm, setUnPublishConfirm] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [addNew, setAddNew] = useState(false);
  const [publish, setPublish] = useState(false);
  const [shareToRole, setShareToRole] = useState(false);
  const [shareToUser, setShareToUser] = useState(false);
  const { loading, executeApi } = useApi();
  const dispatch = useDispatch();
  const showToaster = useToaster();
  const { userName } = useUser();

  const handleAction = async (event: React.MouseEvent, action: string) => {
    event.stopPropagation();
    handleMenuClose();
    switch (action) {
      case FileActions.Download:
        await handleDownload();
        break;
      case FileActions.MoveToBin:
        setDeleteConfirm(true);
        break;
      case FileActions.Edit:
        setEditDialogOpen(true);
        break;
      case FileActions.AddNew:
        setAddNew(true);
        break;
      case FileActions.Publish:
        setPublish(true);
        break;
      case FileActions.UndoPublish:
        setUnPublishConfirm(true);
        break;
      case FileActions.MakeACopy:
        duplicateFolderOrFile(currentRow);
        break;
      case FileActions.ShareToRole:
        setShareToRole(true);
        break;
      case FileActions.ShareToUser:
        setShareToUser(true);
        break;
    }
  };

  const handleDeleteConfirm = async (confirmed: boolean) => {
    setDeleteConfirm(false);
    if (confirmed && currentRow) {
      await moveToBin(currentRow);
    }
  };

  const handleUnPublishConfirm = async (confirmed: boolean) => {
    setUnPublishConfirm(false);
    if (confirmed && currentRow) {
      await handleUnPublish(currentRow);
    }
  };

  const handleDownload = async () => {
    if (currentRow?.type === FolderFileType[FolderFileType.Folder]) {
      if (currentRow?.folderId && currentRow?.description) {
        setDownloadData({
          userId,
          folderId: currentRow?.folderId,
          folderName: currentRow?.description,
        });
        setDialogOpen(true);
      }
    } else {
      if (currentRow?.fileUrl && currentRow?.description) {
        const blobName = currentRow?.fileUrl.split("/").pop();
        await downloadSingleFileUsingUri(blobName, currentRow?.description);
      }
    }
  };

  const downloadSingleFileUsingUri = async (blobName: string, fileName: string) => {
    const requestData: BlobSasRequestDto = {
      BlobName: blobName,
      FileName: fileName,
    };
    const sasUri = await executeApi<BlobSasRequestDto, string>(getBlobSasUri, requestData);
    const a = document.createElement("a");
    a.href = sasUri;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    let actionDescription = FileActions.Download.toString();

    logAuditTrace(
      {
        userId: userId,
        objectType: FolderFileType[FolderFileType.File],
        objectId: currentRow?.fileId,
        objectName: fileName,
        action: actionDescription,
        navigationDateTime: getCurrentESTTime(),
      },
      dispatch
    );
  };

  const duplicateFolderOrFile = async (currentRow: GetAllFilesAndFoldersByUserDto) => {
    if (currentRow?.description?.length > GLOBALSETTINGS.DESCRIPTION_LENGTH) {
      const message = DESCRIPTION_LENGTH(currentRow?.type, GLOBALSETTINGS.DESCRIPTION_LENGTH);
      showToaster(message, SEVERITIES.ERROR);
      setEditDialogOpen(true);
      return;
    }
    const duplicateDto: DuplicateDto = {
      userId: userId,
      folderId:
        currentRow?.type === FolderFileType[FolderFileType.Folder] ? currentRow?.folderId : null,
      parentFolderId:
        currentRow?.type === FolderFileType[FolderFileType.Folder]
          ? currentRow?.parentFolderId
          : null,
      fileId:
        currentRow?.type === FolderFileType[FolderFileType.Folder] ? null : currentRow?.fileId,
    };

    if (currentRow?.type === FolderFileType[FolderFileType.Folder]) {
      const result = await executeApi<DuplicateDto, ResponseDto<GetFoldersByUserIdDto>>(
        duplicateFolder,
        duplicateDto
      );
      handleDuplicateFolder(
        Boolean(result && result.isSuccess && result.data),
        result?.data ?? null,
        currentRow?.type,
        result?.errorMessage ?? null,
        null
      );
    } else {
      const result = await executeApi<DuplicateDto, ResponseDto<DuplicateFilesDto | null>>(
        duplicateFile,
        duplicateDto
      );

      handleDuplicateFolder(
        Boolean(result && result.isSuccess && result.data),
        null,
        currentRow?.type ?? "",
        result?.errorMessage ?? null,
        result.data
      );
    }
  };

  const handleDuplicateFolder = (
    result: boolean,
    folderRow: GetFoldersByUserIdDto | null,
    type: string,
    errorMessage: string | null,
    fileRow: DuplicateFilesDto | null
  ) => {
    if (result) {
      const successMessage =
        type === FolderFileType[FolderFileType.Folder]
          ? DUPLICATEVALIDATIONS.DUPLICATE_FOLDER_SUCCESS
          : DUPLICATEVALIDATIONS.DUPLICATE_FILE_SUCCESS;
      showToaster(successMessage, SEVERITIES.SUCCESS);
      const currentSelectedParentFolderId =
        currentRow?.parentFolderId === 0 ? null : currentRow?.parentFolderId;

      if (type === FolderFileType[FolderFileType.Folder] && folderRow !== null) {
        folderRow.parentFolderId = currentSelectedParentFolderId ?? null;
        dispatch(setFolder({ folderDetails: folderRow, fromGrid: false }));
      }

      // The duplicate is created with the source row's role permissions cloned verbatim, so the
      // copy grants this user exactly what the source did. The copy-folder response carries those
      // flags, but the copy-file response has none, so fall back to the source row for files.
      // Anything unknown stays false rather than exposing an action the user is not assigned.
      const copyPermissionSource = folderRow ?? currentRow;

      const addedRow: GetAllFilesAndFoldersByUserDto = {
        roleFolderPermissionsId: 0,
        folderId: folderRow?.folderId ?? -1,
        parentFolderId: currentSelectedParentFolderId,
        fileId: type === FolderFileType[FolderFileType.File] ? fileRow.newFileId : -1,
        fileUrl: type === FolderFileType[FolderFileType.File] ? fileRow.newFileUrl : null,
        description:
          type === FolderFileType[FolderFileType.File]
            ? fileRow.newFileDescription
            : folderRow.folderName,
        isPublish: currentRow?.isPublish,
        canView: copyPermissionSource?.canView ?? false,
        canEdit: copyPermissionSource?.canEdit ?? false,
        canDelete: copyPermissionSource?.canDelete ?? false,
        canDownload: copyPermissionSource?.canDownload ?? false,
        canManagePermissions: copyPermissionSource?.canManagePermissions ?? false,
        canShareToRole: copyPermissionSource?.canShareToRole ?? false,
        canShareToUser: copyPermissionSource?.canShareToUser ?? false,
        createdBy: userName,
        createdUserId: userId,
        createdDate: getCurrentESTTime(),
        type: type,
        totalRecordCount: 0,
      };

      dispatch(setResourceAdded({ addedRow: addedRow, type: type }));
    } else {
      showToaster(errorMessage || GLOBALMESSAGES.GLOBAL_ERROR, SEVERITIES.ERROR);
    }

    let actionDescription = FileActions.MakeACopy.toString();
    if (!result) {
      actionDescription += GLOBALMESSAGES.FAILED_ADDITION;
    }

    logAuditTrace(
      {
        userId: userId,
        objectType: type,
        objectId:
          type === FolderFileType[FolderFileType.Folder] ? folderRow?.folderId : fileRow.newFileId,
        objectName:
          type === FolderFileType[FolderFileType.Folder]
            ? folderRow?.folderName
            : fileRow.newFileDescription,
        action: actionDescription,
        navigationDateTime: getCurrentESTTime(),
      },
      dispatch
    );
  };

  const handleUnPublish = async (currentRow: GetAllFilesAndFoldersByUserDto) => {
    if (currentRow?.type === null) {
      return;
    }
    const updateData: DeleteOrPublishTypeDto = {
      folderFileType: FolderFileType[currentRow?.type],
      typeId:
        currentRow?.type === FolderFileType[FolderFileType.Folder]
          ? currentRow?.folderId
          : currentRow?.fileId,
      userId: userId,
    };
    const result = await executeApi<DeleteOrPublishTypeDto, boolean>(
      unPublishFolderOrFile,
      updateData
    );
    if (result) {
      currentRow.isPublish = false;

      if (currentRow?.createdUserId !== userId) {
        dispatch(
          setResourceDeleted({
            folderId:
              currentRow?.type === FolderFileType[FolderFileType.Folder]
                ? currentRow?.folderId
                : -1,
            parentFolderId:
              currentRow?.type === FolderFileType[FolderFileType.Folder]
                ? currentRow?.parentFolderId === 0
                  ? null
                  : currentRow?.parentFolderId
                : -1,
            fileId:
              currentRow?.type === FolderFileType[FolderFileType.File] ? currentRow?.fileId : -1,
          })
        );
      } else {
        const modifiedRow: GetAllFilesAndFoldersByUserDto = {
          roleFolderPermissionsId: 0,
          folderId:
            currentRow.type === FolderFileType[FolderFileType.Folder] ? currentRow.folderId : -1,
          parentFolderId: currentRow.parentFolderId ?? null,
          fileId: currentRow.type === FolderFileType[FolderFileType.File] ? currentRow.fileId : -1,
          description: currentRow?.description ?? "",
          isPublish: false,
          canView: currentRow.canView,
          canDelete: currentRow.canDelete,
          canDownload: currentRow.canDownload,
          canEdit: currentRow.canEdit,
          canShareToRole: currentRow.canShareToRole,
          canShareToUser: currentRow.canShareToUser,
          canManagePermissions: currentRow.canManagePermissions,
          totalRecordCount: 0,
        };

        dispatch({ modifiedRow: modifiedRow, type: currentRow?.type });
      }

      showToaster(UNPUBLISH_SUCCESSFUL(), SEVERITIES.SUCCESS);
    } else {
      showToaster(UNPUBLISH_FAIL(currentRow?.type as string), SEVERITIES.ERROR);
    }

    let actionDescription = FileActions.UndoPublish.toString();
    if (!result) {
      actionDescription += GLOBALMESSAGES.FAILED_ADDITION;
    }

    const auditTrace = {
      userId: userId,
      objectType: currentRow?.type,
      objectId:
        currentRow?.type === FolderFileType[FolderFileType.Folder]
          ? currentRow?.folderId
          : currentRow?.fileId,
      objectName: currentRow?.description,
      action: actionDescription,
      navigationDateTime: getCurrentESTTime(),
    };
    if (result && currentRow?.createdUserId !== userId) {
      logAuditTrace(auditTrace, null);
      removeRecentViewedItem(auditTrace, dispatch);
    } else {
      logAuditTrace(auditTrace, dispatch);
    }
  };

  const moveToBin = async (currentRow: GetAllFilesAndFoldersByUserDto) => {
    const deleteTypeDto: DeleteTypeDto = {
      userId: userId,
      typeId:
        currentRow?.type === FolderFileType[FolderFileType.Folder]
          ? currentRow?.folderId
          : currentRow?.fileId,
    };

    const result = await executeApi<DeleteTypeDto, boolean>(
      currentRow?.type === FolderFileType[FolderFileType.Folder] ? deleteFolder : deleteFile,
      deleteTypeDto
    );

    if (result) {
      dispatch(
        setResourceDeleted({
          folderId:
            currentRow?.type === FolderFileType[FolderFileType.Folder] ? currentRow?.folderId : -1,
          parentFolderId:
            currentRow?.type === FolderFileType[FolderFileType.Folder]
              ? currentRow?.parentFolderId === 0
                ? null
                : currentRow?.parentFolderId
              : -1,
          fileId:
            currentRow?.type === FolderFileType[FolderFileType.File] ? currentRow?.fileId : -1,
        })
      );

      showToaster(DELETE_SUCCESSFUL(currentRow?.type as string), SEVERITIES.SUCCESS);
    } else {
      showToaster(DELETE_FAIL(currentRow?.type as string), SEVERITIES.ERROR);
    }

    let actionDescription = FileActions.MoveToBin.toString();
    if (!result) {
      actionDescription += GLOBALMESSAGES.FAILED_ADDITION;
    }

    logAuditTrace(
      {
        userId: userId,
        objectType: currentRow?.type,
        objectId:
          currentRow?.type === FolderFileType[FolderFileType.Folder]
            ? currentRow?.folderId
            : currentRow?.fileId,
        objectName: currentRow?.description,
        action: actionDescription,
        navigationDateTime: getCurrentESTTime(),
      },
      dispatch
    );
  };

  return {
    actions: {
      handleAction,
      downloadData,
      handleDeleteConfirm,
      handleUnPublishConfirm,
    },
    states: {
      dialogOpen,
      deleteConfirm,
      editDialogOpen,
      addNew,
      publish,
      unPublishConfirm,
      loading,
      shareToRole,
      shareToUser,
    },
    setters: {
      setDialogOpen,
      setEditDialogOpen,
      setAddNew,
      setPublish,
      setShareToRole,
      setShareToUser,
      setUnPublishConfirm,
    },
  };
};

export default useMenuActions;
